/**
 * Painel de Detecção de Tópicos
 * Exibe segmentação por tema com timeline visual
 */

import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import FlashOnIcon from '@mui/icons-material/FlashOn'
import RefreshIcon from '@mui/icons-material/Refresh'
import StarIcon from '@mui/icons-material/Star'
import { TopicDetection, Topic } from '../../types/transcription'
import { getTopicDetection } from '../../services/api'
import { contextualErrors, FriendlyError } from '../../services/errorHandler'
import AnalysisNotAvailableCard from './AnalysisNotAvailableCard'

type Props = {
  fileId: string
}

function formatTime(time: string): string {
  // Se já está no formato HH:MM:SS, converte para M:SS ou H:MM:SS
  if (time.includes(':')) {
    const parts = time.split(':')
    if (parts.length === 3) {
      const hours = parseInt(parts[0])
      const mins = parseInt(parts[1])
      const secs = parseInt(parts[2])
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
    return time
  }
  // Fallback para segundos
  const seconds = parseFloat(time)
  if (isNaN(seconds)) return time
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  if (mins > 0) {
    return `${mins}m ${secs}s`
  }
  return `${secs}s`
}

function TopicCard({ topic, isActive, onClick }: { topic: Topic; isActive: boolean; onClick: () => void }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: '2px solid',
        borderColor: isActive ? topic.color : '#e0e0e0',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        bgcolor: isActive ? `${topic.color}10` : '#fff',
        '&:hover': {
          borderColor: topic.color,
          boxShadow: 2
        }
      }}
      onClick={onClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Indicador de cor */}
        <Box
          sx={{
            width: 8,
            height: '100%',
            minHeight: 60,
            bgcolor: topic.color,
            borderRadius: 1
          }}
        />

        <Box sx={{ flex: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {topic.name}
            </Typography>
            <Chip
              label={`${(topic.confidence * 100).toFixed(0)}%`}
              size="small"
              sx={{ 
                fontSize: '0.65rem', 
                height: 20,
                bgcolor: topic.color,
                color: '#fff'
              }}
            />
          </Box>

          {/* Descrição */}
          <Typography variant="body2" sx={{ color: '#666', mb: 1.5, lineHeight: 1.4 }}>
            {topic.description}
          </Typography>

          {/* Timestamps */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: '#888' }} />
              <Typography variant="caption" sx={{ color: '#666' }}>
                {formatTime(topic.startTime)} - {formatTime(topic.endTime)}
              </Typography>
            </Box>
            <Chip
              label={formatDuration(topic.duration)}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
            <Typography variant="caption" sx={{ color: '#888' }}>
              {topic.segments.length} segmentos
            </Typography>
          </Box>

          {/* Keywords */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {topic.keywords.slice(0, 5).map((kw, i) => (
              <Chip
                key={i}
                label={kw}
                size="small"
                variant="outlined"
                sx={{
                  fontSize: '0.65rem',
                  height: 22,
                  borderColor: topic.color,
                  color: topic.color
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Paper>
  )
}

function TopicTimeline({ topics, totalDuration }: { topics: Topic[]; totalDuration: number }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
        Timeline Visual
      </Typography>
      
      <Box
        sx={{
          display: 'flex',
          height: 40,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid #e0e0e0'
        }}
      >
        {topics.map((topic) => {
          const widthPercent = (topic.duration / totalDuration) * 100
          return (
            <Tooltip
              key={topic.id}
              title={`${topic.name} (${formatDuration(topic.duration)})`}
              arrow
            >
              <Box
                sx={{
                  width: `${widthPercent}%`,
                  bgcolor: topic.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  '&:hover': {
                    opacity: 0.8
                  }
                }}
              >
                {widthPercent > 10 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#fff',
                      fontWeight: 600,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      px: 1
                    }}
                  >
                    {topic.name}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          )
        })}
      </Box>

      {/* Escala de tempo */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" sx={{ color: '#888' }}>0:00</Typography>
        <Typography variant="caption" sx={{ color: '#888' }}>
          {formatTime(totalDuration.toString())}
        </Typography>
      </Box>
    </Box>
  )
}

function TransitionCard({ transition }: {
  transition: { fromTopic: string; toTopic: string; timestamp: string; transitionType: 'smooth' | 'abrupt' }
}) {
  const isAbrupt = transition.transitionType === 'abrupt'
  
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        bgcolor: isAbrupt ? '#fff3e0' : '#f5f5f5',
        borderRadius: 1,
        mb: 1
      }}
    >
      <Typography variant="caption" sx={{ color: '#666', minWidth: 40 }}>
        {formatTime(transition.timestamp)}
      </Typography>
      
      <Chip
        label={transition.fromTopic}
        size="small"
        sx={{ fontSize: '0.65rem' }}
      />
      
      {isAbrupt ? (
        <FlashOnIcon sx={{ color: '#f57c00', fontSize: 18 }} />
      ) : (
        <ArrowRightAltIcon sx={{ color: '#888', fontSize: 18 }} />
      )}
      
      <Chip
        label={transition.toTopic}
        size="small"
        sx={{ fontSize: '0.65rem' }}
      />
      
      <Chip
        label={isAbrupt ? 'Abrupta' : 'Suave'}
        size="small"
        variant="outlined"
        sx={{
          fontSize: '0.6rem',
          height: 18,
          ml: 'auto',
          color: isAbrupt ? '#f57c00' : '#4caf50',
          borderColor: isAbrupt ? '#f57c00' : '#4caf50'
        }}
      />
    </Box>
  )
}

export default function TopicDetectionPanel({ fileId }: Props) {
  const [detection, setDetection] = useState<TopicDetection | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null)

  const loadDetection = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getTopicDetection(fileId)
      setDetection(data)
    } catch (e: any) {
      const friendlyError = contextualErrors.analysis(e)
      setError(friendlyError)
      console.error('Erro ao carregar tópicos:', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadDetection()
  }, [fileId])

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={48} />
          <Typography sx={{ mt: 2, color: '#666' }}>
            Detectando tópicos...
          </Typography>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', bgcolor: '#fafafa' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>⚠️</Box>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {error.title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
            {error.message}
          </Typography>
          {error.canRetry && (
            <Button variant="outlined" onClick={loadDetection} startIcon={<RefreshIcon />}>
              Tentar novamente
            </Button>
          )}
        </CardContent>
      </Card>
    )
  }

  if (!detection) {
    return (
      <AnalysisNotAvailableCard 
        title="Detecção de Tópicos" 
        onRetry={loadDetection} 
      />
    )
  }

  const totalDuration = detection.topics.reduce((sum, t) => sum + t.duration, 0)
  const dominantTopicObj = detection.topics.find(t => t.name === detection.dominantTopic)

  return (
    <Box>
      {/* Header com resumo */}
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              📑 Detecção de Tópicos
            </Typography>
            <Button
              startIcon={<RefreshIcon />}
              onClick={loadDetection}
              size="small"
              sx={{ textTransform: 'none' }}
            >
              Atualizar
            </Button>
          </Box>

          {/* Estatísticas rápidas */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {detection.topics.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Tópicos Detectados
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                  {detection.transitions.length}
                </Typography>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Transições
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: dominantTopicObj ? `${dominantTopicObj.color}15` : '#f5f5f5',
                  borderRadius: 2,
                  border: dominantTopicObj ? `2px solid ${dominantTopicObj.color}` : undefined
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon sx={{ color: dominantTopicObj?.color || '#ffc107' }} />
                  <Box>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      Tópico Dominante
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {detection.dominantTopic}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Timeline */}
          <TopicTimeline topics={detection.topics} totalDuration={totalDuration} />

          {/* Distribuição */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Distribuição de Tempo
          </Typography>
          <Grid container spacing={1}>
            {detection.topicDistribution.map((dist) => {
              const topic = detection.topics.find(t => t.id === dist.topicId)
              if (!topic) return null
              return (
                <Grid item xs={12} key={dist.topicId}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 180, fontWeight: 500 }}>
                      {topic.name}
                    </Typography>
                    <Box sx={{ flex: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={dist.percentage}
                        sx={{
                          height: 12,
                          borderRadius: 6,
                          bgcolor: '#f0f0f0',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: topic.color,
                            borderRadius: 6
                          }
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 50, textAlign: 'right' }}>
                      {dist.percentage.toFixed(1)}%
                    </Typography>
                  </Box>
                </Grid>
              )
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Lista de Tópicos */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Tópicos em Ordem Cronológica
              </Typography>

              <Stack spacing={2}>
                {detection.topics.map((topic) => (
                  <TopicCard
                    key={topic.id}
                    topic={topic}
                    isActive={selectedTopic === topic.id}
                    onClick={() => setSelectedTopic(
                      selectedTopic === topic.id ? null : topic.id
                    )}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Transições */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                Transições de Tópico
              </Typography>

              {detection.transitions.map((transition, index) => (
                <TransitionCard key={index} transition={transition} />
              ))}

              {detection.transitions.length === 0 && (
                <Typography sx={{ textAlign: 'center', color: '#666', py: 2 }}>
                  Apenas um tópico detectado
                </Typography>
              )}

              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  <strong>Legenda:</strong><br />
                  <ArrowRightAltIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> Transição suave<br />
                  <FlashOnIcon sx={{ fontSize: 14, verticalAlign: 'middle', color: '#f57c00' }} /> Transição abrupta
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dica */}
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="caption" sx={{ color: '#666' }}>
          💡 <strong>Dica:</strong> A timeline visual mostra a proporção de tempo dedicada a cada tópico.
          Clique em um tópico para ver mais detalhes. Transições abruptas podem indicar mudanças
          bruscas de assunto durante a conversa.
        </Typography>
      </Box>
    </Box>
  )
}
